/* eslint-env jest */
import { stampEntryMethod } from './stampEntryMethod';

// The bug this pins: `customer.cart.isDirty is not a function`, thrown on the
// Continue button of the access-code form.
//
// A cart restored from localStorage is a PLAIN OBJECT -- CustomerContext's
// rehydrate leaves the model reconstruction commented out, and the G1 self-heal
// only replaces a cart that is dead. So a returning visitor with a live cart
// carries attributes and no methods, and the unguarded isDirty() call took the
// whole submit down.
describe('stampEntryMethod', () => {
  const modelCart = (attrs = {}) => ({
    id: 7,
    isDirty: jest.fn(() => true),
    save: jest.fn(),
    ...attrs,
  });

  describe('a real Cart model', () => {
    it('stamps the entry method and saves', () => {
      const cart = modelCart();

      expect(stampEntryMethod(cart)).toBe(true);
      expect(cart.entryMethod).toBe('typed_code');
      expect(cart.save).toHaveBeenCalled();
    });

    it('records where the shopper came from when we know', () => {
      const cart = modelCart();

      stampEntryMethod(cart, { createdLocation: 'sitting_cards' });

      expect(cart.createdLocation).toBe('sitting_cards');
    });

    // This is the highest-traffic entry form; a resubmit or a typo must not
    // re-save an unchanged cart.
    it('does not save when nothing changed', () => {
      const cart = modelCart({ isDirty: jest.fn(() => false) });

      expect(stampEntryMethod(cart)).toBe(false);
      expect(cart.save).not.toHaveBeenCalled();
    });
  });

  describe('a cart rehydrated from localStorage', () => {
    // The crash, exactly: attributes but no methods.
    const plainCart = () => ({ id: 7, createdLocation: null, entryMethod: null });

    it('does not throw', () => {
      expect(() => stampEntryMethod(plainCart())).not.toThrow();
    });

    it('reports that nothing was saved, rather than pretending it was', () => {
      expect(stampEntryMethod(plainCart())).toBe(false);
    });

    // Still stamped in memory: if anything later swaps in a real model for the
    // same row, the value is already there to persist.
    it('still stamps the value locally', () => {
      const cart = plainCart();

      stampEntryMethod(cart, { createdLocation: 'proofs' });

      expect(cart.entryMethod).toBe('typed_code');
      expect(cart.createdLocation).toBe('proofs');
    });

    // Half a model is the same hazard as none.
    it('does not save a cart that can report dirty but cannot save', () => {
      const cart = { id: 7, isDirty: () => true };

      expect(() => stampEntryMethod(cart)).not.toThrow();
      expect(stampEntryMethod(cart)).toBe(false);
    });
  });

  // THE GAP THIS CLOSES. Skipping the plain-object cart lost the typed_code
  // stamp for every returning visitor: about a third of the fall 2026
  // home-page carts that were bought arrived with no entry method at all.
  // Given a loader, the form now fetches the real model for the same row and
  // saves the stamp on that -- the pattern CustomerContext already uses.
  describe('a rehydrated cart, with a loader', () => {
    const plainCart = () => ({ id: 7, createdLocation: null, entryMethod: null });
    const loaded = () => ({ id: 7, save: jest.fn(() => Promise.resolve()) });

    it('loads the cart by id and saves the stamp on the model', async () => {
      const model = loaded();
      const loadCart = jest.fn(() => Promise.resolve(model));

      expect(stampEntryMethod(plainCart(), { createdLocation: 'proofs', loadCart })).toBe(true);
      await Promise.resolve();
      await Promise.resolve();

      expect(loadCart).toHaveBeenCalledWith(7);
      expect(model.entryMethod).toBe('typed_code');
      expect(model.createdLocation).toBe('proofs');
      expect(model.save).toHaveBeenCalled();
    });

    // The stored copy may say typed_code while the row does not (an earlier
    // save failed), so it must not short-circuit: the loaded row decides.
    it('re-saves when the stored copy says typed_code but the row does not', async () => {
      const model = loaded();
      const loadCart = jest.fn(() => Promise.resolve(model));

      stampEntryMethod({ id: 7, entryMethod: 'typed_code' }, { loadCart });
      await Promise.resolve();
      await Promise.resolve();

      expect(model.save).toHaveBeenCalled();
    });

    it('does not save when the row is already stamped', async () => {
      const model = { ...loaded(), entryMethod: 'typed_code', createdLocation: 'proofs' };
      const loadCart = jest.fn(() => Promise.resolve(model));

      stampEntryMethod(plainCart(), { createdLocation: 'proofs', loadCart });
      await Promise.resolve();
      await Promise.resolve();

      expect(model.save).not.toHaveBeenCalled();
    });

    it('logs a failed load instead of breaking the purchase path', async () => {
      const warn = jest.spyOn(console, 'warn').mockImplementation(() => {});
      const loadCart = jest.fn(() => Promise.reject(new Error('offline')));

      expect(() => stampEntryMethod(plainCart(), { loadCart })).not.toThrow();
      await new Promise((resolve) => { setTimeout(resolve, 0); });

      expect(warn).toHaveBeenCalledWith('[stampEntryMethod] could not save the entry method', expect.any(Error));
      warn.mockRestore();
    });

    it('cannot load a cart that has no id yet', () => {
      const loadCart = jest.fn();

      expect(stampEntryMethod({ entryMethod: null }, { loadCart })).toBe(false);
      expect(loadCart).not.toHaveBeenCalled();
    });
  });

  it('does nothing at all without a cart', () => {
    expect(stampEntryMethod(null)).toBe(false);
    expect(stampEntryMethod(undefined)).toBe(false);
  });
});
